import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Icon } from '../../atoms/Icon'
import { ExpandArrow } from '../../atoms/ExpandArrow'
import type { DataTableColumn, SortConfig } from './DataTable.types'

interface MobileSortDropdownProps<T> {
  columns: DataTableColumn<T>[]
  sort: SortConfig[]
  onSort: (field: string) => void
  onSortChange?: (field: string, direction: 'asc' | 'desc') => void
}

const SortContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.xsmall}
    ${({ theme }) => theme.spacing.small};
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  margin-bottom: ${({ theme }) => theme.spacing.small};
  position: relative;
  z-index: 10;
`

const SortLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.tertiary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
`

const SortDropdownWrapper = styled.div`
  position: relative;
  flex: 1;
`

const SortButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.small};
  width: 100%;
  padding: ${({ theme }) => theme.spacing.xsmall}
    ${({ theme }) => theme.spacing.small};
  background: ${({ theme }) => theme.colors.background.secondary};
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  min-height: 32px;
  transition: all ${({ theme }) => theme.transitions.default};

  svg {
    color: ${({ theme }) => theme.colors.text.tertiary};
  }

  &:active {
    background: ${({ theme }) => theme.colors.background.tertiary};
  }

  &[aria-expanded='true'] {
    background: ${({ theme }) => theme.colors.primaryLight};
    border-color: ${({ theme }) => theme.colors.primary};

    svg {
      color: ${({ theme }) => theme.colors.primary};
    }
  }
`

const SortButtonText = styled.span`
  flex: 1;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const SortDropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  box-shadow:
    0 4px 20px rgba(0, 0, 0, 0.12),
    0 2px 8px rgba(0, 0, 0, 0.08);
  z-index: 100;
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  transform: translateY(${({ $isOpen }) => ($isOpen ? 0 : '-8px')})
    scale(${({ $isOpen }) => ($isOpen ? 1 : 0.95)});
  transform-origin: top center;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  padding: ${({ theme }) => theme.spacing.xsmall};
`

const SortOption = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.small};
  width: 100%;
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.medium};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.primaryLight : 'transparent'};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  font-weight: ${({ $active, theme }) =>
    $active
      ? theme.typography.fontWeight.semibold
      : theme.typography.fontWeight.normal};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.primary : theme.colors.text.primary};
  cursor: pointer;
  text-align: left;
  min-height: ${({ theme }) => theme.touchTargets.minimum};
  transition: all ${({ theme }) => theme.transitions.fast};
  margin-bottom: 2px;

  &:last-child {
    margin-bottom: 0;
  }

  &:active {
    background: ${({ theme }) => theme.colors.background.secondary};
    transform: scale(0.98);
  }

  svg {
    color: ${({ theme }) => theme.colors.primary};
    flex-shrink: 0;
  }
`

const SortDirectionToggle = styled.button<{ $isDesc?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: ${({ $isDesc, theme }) =>
    $isDesc ? theme.colors.primaryLight : theme.colors.background.secondary};
  border: 1px solid
    ${({ $isDesc, theme }) => ($isDesc ? theme.colors.primary : 'transparent')};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  color: ${({ $isDesc, theme }) =>
    $isDesc ? theme.colors.primary : theme.colors.text.secondary};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.default};
  flex-shrink: 0;

  @media (hover: none) and (pointer: coarse) {
    width: 44px;
    height: 44px;
  }

  svg {
    width: 14px;
    height: 14px;
    transition: transform ${({ theme }) => theme.transitions.default};
    transform: ${({ $isDesc }) =>
      $isDesc ? 'rotate(180deg)' : 'rotate(0deg)'};
  }

  &:active {
    transform: scale(0.95);
    background: ${({ theme }) => theme.colors.primaryLight};
  }
`

export function MobileSortDropdown<T>({
  columns,
  sort,
  onSort,
  onSortChange,
}: MobileSortDropdownProps<T>) {
  const { t } = useTranslation('ui')
  const [isOpen, setIsOpen] = useState(false)
  const [localField, setLocalField] = useState<string | null>(null)
  const [localDirection, setLocalDirection] = useState<'asc' | 'desc'>('desc')
  const dropdownRef = useRef<HTMLDivElement>(null)

  const sortableColumns = columns.filter((col) => col.sortable)
  const currentSort = sort[0]
  const currentColumn = localField
    ? columns.find((col) => col.key === localField)
    : null

  // Sync local state with parent sort
  useEffect(() => {
    if (currentSort) {
      setLocalField(currentSort.field)
      setLocalDirection(currentSort.order)
    }
  }, [currentSort])

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [isOpen])

  if (sortableColumns.length === 0) {
    return null
  }

  const handleSelectColumn = (field: string) => {
    setLocalField(field)
    setLocalDirection('desc')
    onSort(field)
    setIsOpen(false)
  }

  const handleToggleDirection = (e: React.MouseEvent) => {
    e.stopPropagation()

    if (!localField) return

    const newDirection = localDirection === 'asc' ? 'desc' : 'asc'
    setLocalDirection(newDirection)

    if (onSortChange) {
      onSortChange(localField, newDirection)
    } else {
      // Fallback: use onSort to cycle through sort states
      // onSort typically cycles: asc -> desc -> none -> asc
      // This is a best-effort fallback when onSortChange is not provided
      onSort(localField)
    }
  }

  return (
    <SortContainer>
      <SortLabel>{t('dataTable.mobile.sortBy')}</SortLabel>
      <SortDropdownWrapper ref={dropdownRef}>
        <SortButton
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          <SortButtonText>
            {currentColumn?.label || t('dataTable.mobile.selectColumn')}
          </SortButtonText>
          <ExpandArrow $expanded={isOpen} direction="down" />
        </SortButton>

        <SortDropdownMenu $isOpen={isOpen} role="listbox">
          {sortableColumns.map((column) => (
            <SortOption
              key={column.key}
              $active={localField === column.key}
              onClick={() => handleSelectColumn(column.key)}
              role="option"
              aria-selected={localField === column.key}
            >
              {column.label}
              {localField === column.key && <Icon name="check" size="small" />}
            </SortOption>
          ))}
        </SortDropdownMenu>
      </SortDropdownWrapper>

      {localField && (
        <SortDirectionToggle
          $isDesc={localDirection === 'desc'}
          onClick={handleToggleDirection}
          aria-label={
            localDirection === 'desc'
              ? t('dataTable.mobile.sortAscending')
              : t('dataTable.mobile.sortDescending')
          }
        >
          <Icon name="arrow-up" size="small" />
        </SortDirectionToggle>
      )}
    </SortContainer>
  )
}

export default MobileSortDropdown
